import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  EmployeeTimeoffPoliciesFromLeavesDocument,
  EmployeeTimeoffPoliciesFromLeavesQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import {
  getNestedString,
  type OneMutationNotification,
  useOneMutationSubscription,
} from 'modules/ai/lib/useOneMutationSubscription'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateToString } from 'modules/core/lib/dates'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'

import YearNavigation from 'modules/timeoff/components/YearNavigation'
import Months from 'modules/timeoff/components/YearlyCalendar/Months'
import CalendarStore from 'modules/timeoff/components/YearlyCalendar/Months/store'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { FACTORIAL_CREATION_YEAR } from 'modules/timeoff/lib/constants'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { BlockedPeriods } from 'modules/timeoff/store/collections/blockedPeriods/blockedPeriods'
import { Leaves } from 'modules/timeoff/store/collections/leaves'

import Legend from './Legend'
import SkeletonLoader from './SkeletonLoader'

interface Props {
  employeeId: string
  employeeCanCreateLeaves: boolean
  employeeCanApproveLeaves: boolean
  year: number
}

interface CalendarState {
  loading: boolean
  year: number
  employeeTimeoffPoliciesFromLeavesData: EmployeeTimeoffPoliciesFromLeavesQuery | null
}

const Calendar = ({
  employeeId,
  year,
  employeeCanCreateLeaves,
  employeeCanApproveLeaves,
}: Props) => {
  const graphqlClient = useGraphQLClient()
  if (!graphqlClient) {
    throw new Error('GraphQL client not found')
  }
  const [calendarState, setCalendarState] = useState<CalendarState>({
    loading: false,
    year,
    employeeTimeoffPoliciesFromLeavesData: null,
  })
  const { employee: currentEmployee } = useCurrentSession()
  const { startsOn } = useContractsVersion()
  const calendarLeaves: Leaves = useLazyRef(() => new Leaves()).current
  const calendarBlockedPeriods: BlockedPeriods = useLazyRef(() => new BlockedPeriods()).current
  const currentAndFutureLeavesContextValue = useLeaves()

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const actionLabel = employeeCanApproveLeaves
    ? i18n.t('time_off.admin.action')
    : i18n.t('time_off.action')

  const isOwnTimeoff = (id: string): boolean => {
    return currentEmployee?.id === id
  }

  const onClick = () => {
    const id = employeeId
    navigate(
      isOwnTimeoff(id)
        ? navigation.timeOff.leavesNew.fullPath()
        : navigation.employees.id.timeOff.leavesNew.fullPath({ id })
    )
  }

  const employeeLeaves = useCallback(
    () => {
      return calendarLeaves.filter({
        employee_id: employeeId,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const employeeBlockedPeriods = useCallback(
    () => {
      return calendarBlockedPeriods.toArray()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const fetchData = useCallback(
    async (targetYear: number, { showSkeleton }: { showSkeleton: boolean }) => {
      setCalendarState({
        loading: showSkeleton,
        year: targetYear,
        employeeTimeoffPoliciesFromLeavesData: null,
      })

      // There is a special case for those allowance using natural days. If the
      // first day of the year is during the weekend and there is a leave on the
      // last day of the previous year, we need to get this leave in order to
      // display it properly in this year
      const firstDayOfYear = moment([targetYear, 0, 1])
      const firstDayWeekday = firstDayOfYear.isoWeekday()
      const isFirstDayWeekend = [6, 7].includes(firstDayWeekday)
      const fromDate = isFirstDayWeekend
        ? firstDayOfYear.subtract(firstDayWeekday - 5, 'days')
        : firstDayOfYear
      const result = await Promise.all([
        calendarLeaves
          .fetch({
            data: {
              employee_id: employeeId,
              terminated: true,
              from: dateToString(fromDate, 'backend'),
              to: dateToString(moment([targetYear, 11, 31]), 'backend'),
            },
          })
          .then((leavesData) => {
            return graphqlClient.query({
              query: EmployeeTimeoffPoliciesFromLeavesDocument,
              variables: {
                leaveIds: leavesData.map((leave: { id: string }) => leave.id),
              },
            })
          }),
        calendarBlockedPeriods.fetch({
          data: {
            employee_id: employeeId,
            start_on: dateToString(fromDate, 'backend'),
            finish_on: dateToString(moment([targetYear, 11, 31]), 'backend'),
          },
        }),
      ])
      setCalendarState({
        loading: false,
        year: targetYear,
        employeeTimeoffPoliciesFromLeavesData: result[0].data,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleCreateLeave = useUnsafeCallback(
    async (payload: OneMutationNotification) => {
      const targetEmployeeId = getNestedString(payload.result, [
        'timeoffMutations',
        'createTimeoffLeave',
        'leave',
        'employee',
        'id',
      ])

      if (!targetEmployeeId || (targetEmployeeId && targetEmployeeId !== employeeId)) {
        return
      }

      await fetchData(calendarState.year, { showSkeleton: false })
    },
    [calendarState.year, employeeId, fetchData]
  )

  useOneMutationSubscription('CreateLeave', handleCreateLeave)

  useEffect(() => {
    // Fetches the leaves to display in the calendar whenever there's a change
    // in the context's currentAndFutureLeaves (because the user updated a
    // leave for example)
    fetchData(calendarState.year, { showSkeleton: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAndFutureLeavesContextValue.fetchCount])

  const maxYear = moment().year() + 1
  const minYear = (startsOn && startsOn.year()) || FACTORIAL_CREATION_YEAR

  const store = useUnsafeMemo(() => {
    return new CalendarStore({
      year: calendarState.year,
      leaves: employeeLeaves(),
      blockedPeriods: employeeBlockedPeriods(),
      employeeTimeoffPoliciesFromLeavesData: calendarState.employeeTimeoffPoliciesFromLeavesData,
    })
  }, [
    calendarState.year,
    employeeLeaves().length,
    employeeBlockedPeriods().length,
    calendarState.employeeTimeoffPoliciesFromLeavesData,
  ])

  if (calendarState.loading) {
    return <SkeletonLoader />
  }

  return (
    <Box padding="s32" gap="s32">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <YearNavigation
          onChange={(changedYear) => fetchData(changedYear, { showSkeleton: true })}
          type="quaternary"
          year={calendarState.year}
          minYear={minYear}
          maxYear={maxYear}
        />
        {employeeCanCreateLeaves && (
          <Button onClick={onClick} type="primary" iconName="add">
            {actionLabel}
          </Button>
        )}
      </Box>
      <Months
        dateTree={store.getTree}
        year={calendarState.year}
        employeeTimeoffPoliciesFromLeavesData={calendarState.employeeTimeoffPoliciesFromLeavesData}
      />
      <Legend employeeLeaves={employeeLeaves()} blockedPeriods={employeeBlockedPeriods()} />
    </Box>
  )
}

export default observer(Calendar)
